import React, { useMemo } from 'react';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import { getIntl } from '../../../utils/intl';
import FadeIn from '../../v2BaseComponents/FadeIn';
import Divider from '../../v2BaseComponents/Divider';
import { Loader } from '../../v2BaseComponents/Loader';
import MessageInput from '../../v2BaseComponents/MessageInput';

const intl = getIntl('comments-list', {
    noComments: 'No Comments Yet...',
    placeholder: 'Add a Comment?',
});

export type CommentListOwnProps<TCommentItem> = {
    comments: TCommentItem[];
    getCommentId: (item: TCommentItem) => string;
    renderComment: (item: TCommentItem) => React.ReactNode;
    isSubmittingComment: boolean;
    onCommentSubmit: (comment: string) => void;
    disableNoCommentsText?: boolean;
    /** Supplied by the caller, since note comments and activity comments are validated against different ceilings. */
    maxCommentLength?: number;
};

export type CommentListProps<TCommentItem> = CommentListOwnProps<TCommentItem>;

export default function CommentList<TCommentItem>(props: CommentListProps<TCommentItem>) {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialCommentCount = useMemo(() => props.comments.length, []);

    const disableMarginTop = props.comments.length === 0 && !props.isSubmittingComment && props.disableNoCommentsText;

    return (
        <Stack vertical fullWidth space={contentStackSpacing}>
            {!props.comments.length && !props.isSubmittingComment ? (
                !props.disableNoCommentsText ? (
                    <Stack horizontal justify="center" align={'center'}>
                        <Text variant="body-1">{intl('noComments')}</Text>
                    </Stack>
                ) : null
            ) : (
                <Stack fullWidth vertical space={contentStackSpacing}>
                    {props.comments.map((comment, index) => (
                        <Stack fullWidth vertical space={contentStackSpacing} key={props.getCommentId(comment)}>
                            <FadeIn disableFade={index < initialCommentCount}>{props.renderComment(comment)}</FadeIn>
                            {index < props.comments.length - 1 ? <Divider horizontal /> : null}
                        </Stack>
                    ))}
                    {props.isSubmittingComment ? (
                        <Stack horizontal fullWidth justify={'center'} align={'center'}>
                            <Loader size="sm" />
                        </Stack>
                    ) : null}
                </Stack>
            )}
            <MessageInput
                placeholder={intl('placeholder')}
                onSubmit={(text) => {
                    props.onCommentSubmit(text);
                }}
                isLoading={props.isSubmittingComment}
                maxLength={props.maxCommentLength}
                testID="comment-input"
                style={{ marginTop: disableMarginTop ? 0 : 16 }}
                submitOnEnter={false}
            />
        </Stack>
    );
}
